
import React, { useState, useEffect } from 'react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Store } from 'lucide-react';
import { useForm } from "react-hook-form";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Store as StoreType } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fetchStores } from '@/services/store/storeCore';
import { useLanguage } from '@/contexts/LanguageContext';

interface UserStore {
  id: string;
  store_id: string;
  is_default: boolean;
}

const StoreSelector: React.FC = () => {
  const [stores, setStores] = useState<StoreType[]>([]);
  const [userStores, setUserStores] = useState<UserStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const form = useForm({
    defaultValues: {
      store_id: "",
      is_default: false,
    },
  });

  useEffect(() => {
    if (user) {
      fetchUserStores();
      loadAvailableStores();
    }
  }, [user]);

  const loadAvailableStores = async () => {
    try {
      const allStores = await fetchStores();
      setStores(allStores);
    } catch (error: any) {
      toast({
        title: t('storeSelector.errorLoadStores'),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchUserStores = async () => {
    try {
      setLoading(true);
      // Precisamos verificar se a tabela user_stores existe antes de fazer a consulta
      const { data, error } = await supabase
        .from('user_stores')
        .select('id, store_id, is_default')
        .eq('user_id', user?.id);

      if (error) throw error;
      
      setUserStores(data || []);
    } catch (error: any) {
      toast({
        title: t('storeSelector.errorLoadUserStores'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: { store_id: string, is_default: boolean }) => {
    if (!user) return;

    try {
      // Verificar se o usuário já tem essa loja associada
      const exists = userStores.some(us => us.store_id === values.store_id);
      
      if (exists) {
        toast({
          title: t('storeSelector.storeAlreadyAdded'),
          description: t('storeSelector.storeAlreadyAddedDesc'),
          variant: "destructive",
        });
        return;
      }

      // Adicionar nova loja ao usuário
      const { error } = await supabase
        .from('user_stores')
        .insert({
          user_id: user.id,
          store_id: values.store_id,
          is_default: values.is_default
        });

      if (error) throw error;

      if (values.is_default) {
        // Se a nova loja for definida como padrão, remover o padrão das outras
        await supabase
          .from('user_stores')
          .update({ is_default: false })
          .neq('store_id', values.store_id)
          .eq('user_id', user.id);

        // Atualizar também no perfil do usuário
        await supabase
          .from('profiles')
          .update({ main_store_id: values.store_id })
          .eq('id', user.id);
      }

      toast({
        description: t('storeSelector.storeAddedSuccess'),
      });

      // Recarregar lojas, limpar o formulário e fechar
      await fetchUserStores();
      form.reset();
      setFormOpen(false);
    } catch (error: any) {
      toast({
        title: t('storeSelector.errorSaveStore'),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_stores')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        description: t('storeSelector.storeRemovedSuccess'),
      });

      await fetchUserStores();
    } catch (error: any) {
      toast({
        title: t('storeSelector.errorRemoveStore'),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSetDefault = async (storeId: string, userStoreId: string) => {
    try {
      // Definir a loja selecionada como padrão
      await supabase
        .from('user_stores')
        .update({ is_default: true })
        .eq('id', userStoreId);

      // Remover o padrão das outras lojas
      await supabase
        .from('user_stores')
        .update({ is_default: false })
        .neq('id', userStoreId)
        .eq('user_id', user?.id || '');

      // Atualizar também no perfil do usuário
      await supabase
        .from('profiles')
        .update({ main_store_id: storeId })
        .eq('id', user?.id || '');

      toast({
        description: t('storeSelector.defaultStoreSet'),
      });

      await fetchUserStores();
    } catch (error: any) {
      toast({
        title: t('storeSelector.errorSetDefault'),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStoreName = (storeId: string) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || t('storeSelector.unknownStore');
  };

  const getStoreLocation = (storeId: string) => {
    const store = stores.find(s => s.id === storeId);
    return store?.location || "";
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div>
      </div>
    );
  }

  return (
    <Card className="bg-gray-800 text-white border-gray-700">
      <CardHeader>
        <CardTitle>{t('storeSelector.title')}</CardTitle>
        <CardDescription className="text-gray-300">{t('storeSelector.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {userStores.length === 0 && !formOpen ? (
          <div className="text-center py-6 text-gray-400">
            <p className="mb-4">{t('storeSelector.noStores')}</p>
            <Button
              onClick={() => setFormOpen(true)}
              className="bg-brand-red text-white hover:bg-brand-red/80 border-transparent"
            >
              <Store size={16} className="mr-2" /> {t('storeSelector.addStore')}
            </Button>
          </div>
        ) : (
          <>
            {!formOpen && (
              <div className="mb-4">
                <Button
                  onClick={() => {
                  form.reset({
                    store_id: "",
                    is_default: userStores.length === 0, // Se for a primeira loja, define como padrão
                  });
                  setFormOpen(true);
                }}
                  className="bg-brand-red text-white hover:bg-brand-red/80 border-transparent"
                >
                  <Store size={16} className="mr-2" /> {t('storeSelector.addNewStore')}
                </Button>
              </div>
            )}

            {formOpen && (
              <>
                <Separator className="my-4 bg-gray-700" />
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="store_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('storeSelector.selectStore')}</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="bg-gray-900 border-gray-700">
                                <SelectValue placeholder={t('storeSelector.selectStorePlaceholder')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-gray-900 border-gray-700">
                              {stores.map(store => (
                                <SelectItem key={store.id} value={store.id}>
                                  {store.name} {store.location ? `- ${store.location}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="is_default"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-700 p-3 shadow-sm">
                          <div className="space-y-0.5">
                            <FormLabel>{t('storeSelector.defaultStore')}</FormLabel>
                            <FormDescription className="text-gray-400">
                              {t('storeSelector.defaultStoreDescription')}
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setFormOpen(false);
                          form.reset();
                        }}
                      >
                        {t('storeSelector.cancel')}
                      </Button>
                      <Button type="submit">
                        {t('storeSelector.addStoreButton')}
                      </Button>
                    </div>
                  </form>
                </Form>
                <Separator className="my-4 bg-gray-700" />
              </>
            )}

            {userStores.length > 0 && (
              <div className="space-y-4 mt-4">
                {userStores.map((userStore) => {
                  const storeName = getStoreName(userStore.store_id);
                  const storeLocation = getStoreLocation(userStore.store_id);
                  
                  return (
                    <div
                      key={userStore.id}
                      className="p-4 border border-gray-700 rounded-lg relative hover:bg-gray-750 transition-colors"
                    >
                      {userStore.is_default && (
                        <Badge className="absolute top-2 right-2 bg-brand-red">
                          <Store size={14} className="mr-1" /> {t('storeSelector.defaultBadge')}
                        </Badge>
                      )}
                      <p className="font-medium mb-1">{storeName}</p>
                      {storeLocation && (
                        <p className="text-sm text-gray-400"><MapPin size={14} className="inline mr-1" /> {storeLocation}</p>
                      )}

                      <div className="flex gap-2 mt-3">
                        {!userStore.is_default && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleSetDefault(userStore.store_id, userStore.id)}
                            >
                              <Check size={14} className="mr-1" /> {t('storeSelector.setAsDefault')}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemove(userStore.id)}
                            >
                              {t('storeSelector.remove')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StoreSelector;
